import { ExclamationCircleIcon, XIcon } from "@heroicons/react/outline";
import { CheckCircleIcon } from "@heroicons/react/solid";
import DocumentSvg from "../../Icons/DocumentSvg";
import { ButtonBase, ModalBase } from "@reusejs/react";
import { useState } from "react";
import LoginModalComponent from "./LoginModal";
import { getDocumentById } from "services";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { PDFDocument, degrees, rgb } from "pdf-lib";

export interface DocumentsProps {
  property: any;
}
export default function Documents(props: DocumentsProps) {
  const router = useRouter();
  const [download, setDownload] = useState(null);
  const userToken = useSelector((state: any) => {
    return state.auth.accessToken;
  });

  const LoginModalWrapper = (props: any) => {
    return (
      <LoginModalComponent
        heading="Login to downlaod"
        subHeading="Please login or create account to download files."
        description="Property documents are confidential documents only available to Auction bazaar users."
        router={router}
        {...props}
      />
    );
  };
  const openModal = async () => {
    await ModalBase(
      {
        content: LoginModalWrapper,
        timeout: 0,
        backgroundColor: "bg-black/30",
        backgroundOpacity: "opacity-70",
        modalBaseClasses: {
          background: "bg-white",
          border: "border-2 rounded-lg",
          alignment: "z-50  overflow-visible",
          padding: "p-0",
          small: "max-w-4xl",
        },
      },
      1000,
      "__next"
    );
  };

  const addWatermarkToPDF = async (
    pdfBuffer: any,
    watermark: any,
    keyPath: string
  ) => {
    try {
      // Load the existing PDF
      const pdfDoc = await PDFDocument.load(pdfBuffer);

      // Add a watermark to each page
      const pages = pdfDoc.getPages();
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        // Add text as a watermark
        const { width, height } = page.getSize();
        page.drawText(watermark, {
          x: width / 2 - 150,
          y: height / 2,
          color: rgb(0.7, 0.7, 0.7),
          size: 100,
          rotate: degrees(45),
        });
      }
      // Save the modified PDF to a buffer
      const modifiedPdfBuffer = await pdfDoc.save();
      downloadPDF(modifiedPdfBuffer, keyPath);
    } catch (error) {
      console.error("Error adding watermark:", error);
    }
  };

  const downloadPDF = (pdfBuffer: any, fileName: string) => {
    const blob = new Blob([pdfBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchAndProcessPDF = async (url: string, key_path: string) => {
    try {
      const response = await fetch(url);
      if (response?.url?.includes("pdf")) {
        const pdfBuffer = await response.arrayBuffer();
        await addWatermarkToPDF(pdfBuffer, "Auction Bazaar", key_path);
      } else {
        router.push(`${url}?dl`);
      }
    } catch (error) {
      console.error("Error fetching or processing PDF:", error);
    }
  };

  const handleDownload = async (item: any) => {
    setDownload(null);
    if (userToken) {
      const data: any = await getDocumentById(item.uuid);
      fetchAndProcessPDF(
        `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${data.key_path}`,
        data?.key_path
      );
      setDownload(item.file_name);
    } else {
      openModal();
    }
  };

  return (
    <div className="bg-white py-2 px-4 sm:px-6 lg:px-8">
      <div className="relative border-b max-w-lg mx-auto lg:max-w-7xl">
        <div className="text-left">
          <h2 className="text-xl tracking-tight font-semibold text-gray-900 sm:tracking-tight">
            Documents
          </h2>
          <p className="mt-2 text-base text-gray-500 sm:mt-4"></p>
          <p className="mt-2 flex justify-start items-center text-base sm:mt-4">
            Download all files
            {/* in{" "}
            <span className="ml-1 font-semibold text-blue-600 underline">
              zip format
            </span> */}
            <ExclamationCircleIcon height={16} width={16} className="ml-1" />
          </p>
        </div>
        <div className="py-4 lg:grid lg:grid-cols-2 lg:gap-20 lg:items-start">
          <div>
            {props.property.documents && props.property.documents.length > 0 ? (
              props.property.documents?.map((item: any, index: any) => {
                return (
                  <div
                    className="flex flex-col sm:flex-row mb-6 justify-between items-center p-4 border rounded-md "
                    key={index}
                  >
                    <div className="flex items-center mb-2 sm:mb-0">
                      <DocumentSvg color="#000000" />
                      <div className="ml-6">
                        <div className="text-sm">{item.file_name}</div>
                      </div>
                    </div>
                    <ButtonBase
                      variant="primary"
                      type="submit"
                      label="Download"
                      onClick={() => handleDownload(item)}
                      buttonBaseClasses={{
                        font: "text-sm font-medium",
                        backgroundColor: "bg-blue-600 hover:bg-blue-700",
                        textColor: "text-white",
                        border: "any",
                        padding: "px-4 py-3",
                      }}
                    />
                  </div>
                );
              })
            ) : (
              <div className="border border-red-300 animate-pulse shadow rounded-md p-4 max-w-sm w-full bg-red-100">
                <div className="flex justify-center bg-red-100 space-x-4">
                  <div className="flex justify-center text-lg sm:text-xl col-span-3 p-4 text-red-500 bg-red-100">
                    Documents not uploaded!
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="h-full flex py-6">
            {download && (
              <div className="w-full self-end p-3 mx-4 rounded-lg text-sm bg-green-50 text-green-700 flex justify-between">
                <div className="flex items-center">
                  <CheckCircleIcon height={16} width={16} className="mr-2" />
                  Successfully downloaded {download}
                </div>
                <XIcon
                  height={16}
                  width={16}
                  className="font-bold cursor-pointer"
                  onClick={() => {
                    setDownload(null);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
